/**
 * @file DispatchLoginModal.tsx
 * @description Dedicated login modal for delivery dispatch operators with TableGreet brand identity.
 */

import React, { useState } from 'react';
import { Lock, Mail, ArrowRight } from 'lucide-react';
import logo from '../../../assets/logo.svg';

interface DispatchLoginModalProps {
  onLogin: (email: string, pass: string) => Promise<any>;
}

export const DispatchLoginModal: React.FC<DispatchLoginModalProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      await onLogin(email, password);
    } catch {
      setErrorMessage("Account doesn't exist");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0a0a0a]/90 backdrop-blur-md">
      <div className="w-full max-w-md bg-[#121212] border border-white/10 rounded-3xl p-8 shadow-2xl">
        <div className="flex flex-col items-center text-center mb-8">
          <img src={logo} className="w-16 h-16 object-contain mb-4" alt="TableGreet™" />
          <div className="flex items-center gap-1.5">
            <h2 className="text-2xl font-black tracking-tight bg-gradient-to-r from-[#ff4f00] to-orange-400 bg-clip-text text-transparent">
              TableGreet<sup className="text-[0.55em] font-bold align-super">™</sup>
            </h2>
            <span className="text-xs font-extrabold uppercase px-2 py-0.5 rounded bg-[#ff4f00]/15 text-[#ff4f00] border border-[#ff4f00]/30 tracking-wider">
              Dispatch
            </span>
          </div>
          <p className="text-xs text-zinc-400 mt-1.5">
            Delivery & Fleet Operations Terminal Login
          </p>
        </div>

        {errorMessage && (
          <div className="mb-6 p-3.5 rounded-xl bg-rose-950/60 border border-rose-800/80 text-rose-300 text-xs font-semibold">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">
              Staff Email
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="dispatch@restaurant.com"
                className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-[#0a0a0a] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-[#ff4f00] text-sm font-medium transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••••••"
                className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-[#0a0a0a] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-[#ff4f00] text-sm font-medium transition-colors"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full mt-4 py-4 rounded-2xl bg-[#ff4f00] hover:bg-[#e64700] active:scale-[0.98] text-white font-black text-sm tracking-wider uppercase flex items-center justify-center gap-2 shadow-lg shadow-[#ff4f00]/25 disabled:opacity-50 transition-all cursor-pointer"
          >
            <span>{isSubmitting ? 'Accessing Terminal...' : 'Access Dispatch Terminal'}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
};
